import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/data/site";
import About from "@/pages/About";
import Portfolio from "@/pages/Portfolio";
import Testimonials from "@/pages/Testimonials";
import AssociatedWorks from "@/pages/AssociatedWorks";
import Contact from "@/pages/Contact";

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        // small delay so layout is ready
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 50);
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location]);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="Nancy Naden drumming live"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ transform: "scale(1.4) translateY(11%)", transformOrigin: "center 32%" }}
        />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-8 pb-20 md:pb-28 pt-32">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-display text-cream text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black leading-[0.9] tracking-tighter"
          >
            Nancy
            <br />
            Naden
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 text-cream/90 text-lg md:text-2xl uppercase tracking-[0.15em] font-light"
          >
            UK Based
            <span className="mx-3 text-cream/40">|</span>
            Freelance Drummer
            <span className="mx-3 text-cream/40">|</span>
            Hybrid Drumming
          </motion.p>
        </div>
      </section>

      {/* Intro strip */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <p className="text-steel text-xs tracking-[0.2em] uppercase font-semibold mb-6">Hybrid drummer</p>
          <p className="font-display text-2xl sm:text-3xl md:text-4xl leading-snug tracking-tight text-ink">
            Combining acoustic drums with Ableton Live, Logic Pro X and the
            Roland SPD-SX PRO for dynamic, genre-spanning shows that never miss a beat.
          </p>
          <Link
            to="/#about"
            data-testid="home-about-link"
            className="inline-flex items-center gap-2 mt-10 text-steel hover:text-ink font-medium transition-colors duration-300"
          >
            More about Nancy <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>

      {/* Full-page sections */}
      <div id="testimonials"><Testimonials embedded /></div>
      <div id="about"><About embedded /></div>
      <div id="works"><AssociatedWorks embedded /></div>
      <div id="videos"><Portfolio embedded /></div>
      <div id="contact"><Contact embedded /></div>
    </div>
  );
}
